import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';

import Root from './auth/Root';
import type PageProps from './auth/types/inertia/PageProps';
import initSentry from './shared/initSentry';

import translationLoadersByLocale from 'virtual:translations/dashboard';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_TRACES_SAMPLE_RATE = import.meta.env
    .VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined;

const pages = import.meta.glob<ResolvedComponent>(
    './auth/pages/{*.tsx,*/*.tsx}',
    { import: 'default' }
);

if (SENTRY_DSN) {
    initSentry(
        SENTRY_DSN,
        SENTRY_TRACES_SAMPLE_RATE ? parseFloat(SENTRY_TRACES_SAMPLE_RATE) : 0.1
    );
}

createInertiaApp<PageProps>({
    progress: {
        delay: 250,
        color: '#12C19B',
        includeCSS: true,
        showSpinner: false,
    },
    resolve: (name) => {
        /**
         * There's a bug in the current version of Inertia that causes the
         * `resolve` function to be called with `undefined` as the `name`
         * argument. The 'Refresh' page is a workaround for that bug.
         */
        const pageComponent = pages[`./auth/pages/${name ?? 'Refresh'}.tsx`];

        if (!pageComponent) {
            throw new Error(`Page component "${name}" not found.`);
        }

        return pageComponent();
    },

    setup: ({ el, App, props }) => {
        const { bundleLocale } = props.initialPage.props.localization;
        const loadTranslations = translationLoadersByLocale[bundleLocale];

        if (!loadTranslations) {
            throw new Error(`No translations bundled for "${bundleLocale}".`);
        }

        loadTranslations().then(({ default: translations }) => {
            createRoot(el).render(
                <Root App={App} props={props} translations={translations} />
            );
        });
    },
});
